import { DashboardTitle } from "../../components/DashboardTitle";
import { Skeleton, TextField } from "@mui/material";
import { Field, Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "../../api/api";
import { environment } from "../../environments/environment";
import React, { useState, useEffect } from "react";
import { BlackButton } from "../../components/black-button";
import { useNavigate } from "react-router-dom";
import { FaSpinner, FaShoppingCart } from "react-icons/fa";
import { countries, states } from "../../constants";
import { useSelector } from "react-redux";
import { IAppState } from "../../store/store";


const initialValues = {
  first_name: "",
  last_name: "",
  middle_name: "",
  university: "",
  address: "",
  // address_line2: "",
  city: "",
  state: "",
  zip: "",
  country: "United States",
  phone: "",
  email: "",
};

const validationSchema = Yup.object({
  first_name: Yup.string().trim().required("First Name is required"),
  last_name: Yup.string().trim().required("Last Name is required"),
  university: Yup.string().trim().required("University is required"),
});

const getSavedUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const getProfileInitialValues = () => {
  const user = getSavedUser();

  return {
    ...initialValues,
    first_name: user.first_name || "",
    last_name: user.last_name || "",
    university: user.university || "",
  };
};

interface CartItem {
  amount: number;
  campaign_title: string;
  campaign_uuid: string;
  cart_item_uuid: string;
  image_url: string;
  quantity: number;
  size: string;
  title: string;
  unit_price: number;
}

export const CartCheckout = () => {
  const navigate = useNavigate();
  const cart_uuid = localStorage.getItem("cart_uuid");
  const [isLoading, setIsLoading] = useState(false);
  const [orderSummary, setOrderSummary] = useState<any>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartTotal, setCartTotal] = useState<number>(0);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const isLoggedIn = useSelector((state: IAppState) => state.auth.token);
  const [profileInitialValues] = useState(getProfileInitialValues);

  // Fetch order summary when component mounts
  useEffect(() => {
    const fetchOrderSummary = async () => {
      if (!cart_uuid) return;
      
      try {
        setSummaryLoading(true);
        const response = await api.get(`${environment.baseUrl}/checkout?cart_uuid=${cart_uuid}`);
        setCartItems(response.data.data.cart_items);
        setCartTotal(response.data.data.total_amount);
        setOrderSummary(response.data.data);
      } catch (error) {
        console.error("Error fetching order summary:", error);
        toast.error("Could not retrieve your order details");
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchOrderSummary();
  }, [cart_uuid]);

    const handleSubmit = async (
      values: any,
      { setSubmitting }: FormikHelpers<any>
    ) => {
      const savedUser = getSavedUser();
      const requiredProfileFields = ["first_name", "last_name", "university"];
      const profileIsIncomplete = requiredProfileFields.some(
        (field) => !savedUser[field]?.trim()
      );

      if (profileIsIncomplete) {
        try {
          const profileResponse = await api.put(
            `${environment.baseUrl}/profile/edit`,
            {
              ...savedUser,
              first_name: values.first_name.trim(),
              last_name: values.last_name.trim(),
              middle_name: values.middle_name || savedUser.middle_name || "",
              university: values.university.trim(),
              address_line1: values.address || savedUser.address_line1 || "",
              address_line2: savedUser.address_line2 || "",
              city: values.city || savedUser.city || "",
              state: values.state || savedUser.state || "",
              zip: values.zip || savedUser.zip || "",
              country: values.country || savedUser.country || "United States",
              phone: values.phone || savedUser.phone || "",
              email: values.email || savedUser.email || "",
              media_url: savedUser.media_url || null,
            }
          );

          const profileWasSaved =
            profileResponse.data.success || profileResponse.data.code === 200;

          if (!profileWasSaved) {
            toast.error(
              profileResponse.data.message || "Unable to save your profile details"
            );
            setSubmitting(false);
            return;
          }

          localStorage.setItem(
            "user",
            JSON.stringify(profileResponse.data.data || {
              ...savedUser,
              first_name: values.first_name.trim(),
              last_name: values.last_name.trim(),
              university: values.university.trim(),
            })
          );
        } catch (error: any) {
          console.error(error);
          toast.error(
            error.response?.data?.message || "Unable to save your profile details"
          );
          setSubmitting(false);
          return;
        }
      }

      const orderPayload = {
        ...values,
        cart_uuid: cart_uuid,
      };

      // console.log(orderPayload)
      try {
        setIsLoading(true)
        const response = await api.post(
          `${environment.baseUrl}/product/order/buy-yourself`, orderPayload)
        // console.log(response.data.data);
        const checkoutLink = response.data.data.payment_link;
        // navigate(checkoutLink);
        // Open the checkout link in a new tab
        window.location.href = checkoutLink;

        localStorage.removeItem("cart_uuid");
      } catch (error: any) {
        console.error(error)
        toast.error(
          error.response?.data?.message ||
            "Unable to start payment. Please try again."
        )
      } finally {
        setIsLoading(false)
      }
    }

    useEffect(() => {
      // If user is not logged in, redirect to login page
      if (!isLoggedIn) {
        // toast.error("Please log in to continue with checkout");
        navigate("/login");
      }
    }, [isLoggedIn, navigate]);

  return (
    <section className={`${cart_uuid ? "web_padding_x web_padding_y" : ""} !pt-5`}>
      {
        isLoading && (
          <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-40 backdrop-blur-sm z-50 flex items-center justify-center transition-all duration-300">
            <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100 w-80 flex flex-col items-center">
              <div className="bg-gray-50 p-4 rounded-full mb-4 relative">
                <FaShoppingCart className="text-4xl text-black opacity-20" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-12 w-12 rounded-full border-4 border-t-black border-r-gray-300 border-b-gray-300 border-l-gray-300 animate-spin"></div>
                </div>
              </div>
              <h3 className="font-bold text-xl mb-1">Processing Order</h3>
              <p className="text-gray-600 text-center">Please wait while we prepare your order...</p>
              <div className="w-full mt-4 bg-gray-100 h-1.5 rounded-full overflow-hidden">
                <div className="h-full bg-black animate-pulse rounded-full"></div>
              </div>
            </div>
          </div>
        )
      }
          <DashboardTitle
            text={"Order Details"}
          ></DashboardTitle>
    
          <div className="grid md:grid-cols-12 gap-8 mt-8">
        {/* Form Section */}
        <div className="md:col-span-8">
          <Formik
            initialValues={profileInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, isSubmitting, touched }) => (
              <Form>
                <div className="dashboard_subtitle md:mt-0">
                  Your Order Information
                </div>
    
                {/* ...existing form fields... */}
                <div className="grid gap-5 mt-4 md:grid-cols-2 xl:grid-cols-3">
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">First Name</h6>
                    <Field
                      name="first_name"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="First Name...."
                      required
                      error={Boolean(touched.first_name && errors.first_name)}
                      helperText={touched.first_name && errors.first_name}
                    />
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">Middle Name <small> (optional)</small> </h6>
                    <Field
                      name="middle_name"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="Middle Name...."
                    />
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">Last Name </h6>
                    <Field
                      name="last_name"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="Last Name...."
                      required
                      error={Boolean(touched.last_name && errors.last_name)}
                      helperText={touched.last_name && errors.last_name}
                    />
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">University </h6>
                    <Field
                      name="university"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="University...."
                      required
                      error={Boolean(touched.university && errors.university)}
                      helperText={touched.university && errors.university}
                    />
                  </div>
                </div>
    
    
                <div className="grid gap-5 mt-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="">
                    <h6 className="font-bold text-sm md:text-base">Phone </h6>
                    <Field
                      name="phone"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="Phone Number...."
                    />
                  </div>
                <div className="">
                    <h6 className="font-bold text-sm md:text-base">Email </h6>
                    <Field
                      name="email"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="Email...."
                    />
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">Address</h6>
                    <Field
                      name="address"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="Street address"
                    />
                  </div>
                  {/* <div className="">
                    <h6 className="font-bold text-sm md:text-base">Address Line 2</h6>
                    <Field
                      name="address_line2"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="(optional)"
                    />
                  </div> */}
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">City</h6>
                    <Field
                      name="city"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="City"
                    />
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">Country</h6>
                    <Field
                      name="country"
                      as="select"
                      className="rounded-full py-2 w-full border mt-3 pl-3"
                      // classNamePrefix="custom_select"
                    >
                      {countries.map((country, index )=> (
                        <option key={index} value={country}>{country}</option>
                      ))}
                    </Field>
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">State</h6>
                    <Field
                      name="state"
                      as="select"
                      className="rounded-full py-2 w-full border mt-3 pl-3"
                      // classNamePrefix="custom_select"
                    >
                      <option value="">Select State</option>
                      {states.map((state, index) => (
                        <option key={index} value={state}>{state}</option>
                      ))}
                    </Field>
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">ZIP/Postal Code</h6>
                    <Field
                      name="zip"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="ZIP Code"
                    />
                  </div>
                  
                  
                </div>
    
                <div className="mt-5 w-full max-w-[300px] flex flex-col">
                  <BlackButton
                    text="Proceed to Payment"
                    type="submit"
                    isDisabled={isSubmitting}
                    isLoading={isSubmitting}
                  ></BlackButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
        
        {/* Order Summary Section */}
        <div className="md:col-span-4">
          <div className="bg-gray-50 rounded-xl p-5 sticky top-5">
            <h2 className="text-lg font-bold mb-4">Order Summary</h2>
            
            {summaryLoading ? (
              <div className="space-y-3">
                <Skeleton variant="rectangular" height={24} />
                <Skeleton variant="rectangular" height={24} />
                <Skeleton variant="rectangular" height={24} />
                <Skeleton variant="rectangular" height={40} />
              </div>
            ) : orderSummary ? (
              <div>
                {/* Items List */}
                <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
                  {/* {orderSummary.items?.map((item: any, index: number) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200">
                      <div className="flex items-center space-x-3">
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt={item.name} 
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                        </div>
                      </div>
                      <p className="font-medium">${Number(item.price).toFixed(2)}</p>
                    </div>
                  ))} */}
                    {cartItems.map((item, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200">
                        <div className="flex items-center space-x-3">
                          {item.image_url && (
                            <img 
                              src={item.image_url} 
                              alt={item.title} 
                              className="w-12 h-12 object-cover rounded"
                            />
                          )}
                          <div>
                            <p className="font-medium">{item.title}</p>
                            <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                          </div>
                        </div>
                        <p className="font-medium">${Number(item.unit_price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                </div>
                
                {/* Totals */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <p className="font-medium text-lg">Subtotal</p>
                    <p className="font-bold text-lg">${cartTotal.toFixed(2)}</p>
                    </div>
                  {/* <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <p className="font-medium">Shipping</p>
                    <p className="font-medium">Free</p>
                    </div> */}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                No items in your cart
              </div>
            )}
          </div>
        </div>
      </div>
        </section>
  )
}
